import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CustomerAuthProvider, useCustomerAuth } from './context/CustomerAuthContext';
import { SocketProvider } from './contexts/SocketContext';

// Layout & Core (loaded immediately)
import CustomerLayout from './components/CustomerLayout';

// Auth pages (loaded immediately - needed for first paint)
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import CustomerRegisterPage from './pages/CustomerRegisterPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import AcceptInvitePage from './pages/AcceptInvitePage';

// Marketing pages (lazy loaded)
const MarketingHomePage = lazy(() => import('./pages/marketing/MarketingHomePage'));
const BookDemoPage = lazy(() => import('./pages/marketing/BookDemoPage'));

// Lazy loaded pages (code split - loaded on demand)
const CustomerDashboardPage = lazy(() => import('./pages/CustomerDashboardPage'));
const CustomerSchedulePage = lazy(() => import('./pages/CustomerSchedulePage'));
const CustomerClassesPage = lazy(() => import('./pages/CustomerClassesPage'));
const CustomerCommunityPage = lazy(() => import('./pages/CustomerCommunityPage'));
const CustomerSubscriptionPage = lazy(() => import('./pages/CustomerSubscriptionPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PublicSchedulePage = lazy(() => import('./pages/PublicSchedulePage'));
const CheckInPage = lazy(() => import('./pages/CheckInPage'));
const RingsPage = lazy(() => import('./pages/RingsPage'));

// Legacy redirect component - redirects old /s/:studioname URLs to new /:studioname format
const LegacyRedirect = () => {
  const { studioname } = useParams();
  return <Navigate to={`/${studioname}`} replace />;
};

// Suspense fallback for lazy pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div
      className="animate-spin rounded-full h-12 w-12 border-4"
      style={{
        borderColor: 'var(--highlight-15)',
        borderTopColor: 'var(--primary)',
      }}
    ></div>
  </div>
);

// Standalone loading spinner for full page contexts
const FullPageSpinner = () => (
  <div
    className="flex items-center justify-center h-screen"
    style={{ background: 'var(--bg-base)' }}
  >
    <div
      className="animate-spin rounded-full h-16 w-16 border-4"
      style={{
        borderColor: 'var(--highlight-15)',
        borderTopColor: 'var(--primary)',
      }}
    ></div>
  </div>
);

// Protected Route component - customer portal only allows students
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, needsRegistration, isSignedIn, customer } = useCustomerAuth();

  // Keep showing spinner while:
  // 1. Loading state is true, OR
  // 2. User is signed in but we haven't determined their status yet
  //    (API hasn't returned - customer is null AND needsRegistration is false), OR
  // 3. We have cached customer data but auth says not signed in - auth state may still be loading
  //    (this handles race condition where auth briefly returns false before reading session)
  // This prevents the login flash during auth/API race conditions
  const authStateUnknown =
    (isSignedIn && !customer && !needsRegistration) ||
    (!isSignedIn && customer && !needsRegistration);

  if (loading || authStateUnknown) {
    return <FullPageSpinner />;
  }

  // If signed in but no customer record, redirect to complete profile
  if (needsRegistration) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, needsRegistration, isSignedIn, customer } = useCustomerAuth();

  // Keep showing spinner while auth state is still being determined
  // Same logic as ProtectedRoute - wait for auth/customer data to stabilize
  const authStateUnknown =
    (isSignedIn && !customer && !needsRegistration) ||
    (!isSignedIn && customer && !needsRegistration);

  if (loading || authStateUnknown) {
    return <FullPageSpinner />;
  }

  // If signed in but needs to complete profile, redirect to complete-profile
  if (isSignedIn && needsRegistration) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (isAuthenticated) {
    // Check for saved redirect from login (e.g., check-in flow)
    const savedRedirect = localStorage.getItem('postLoginRedirect');
    if (savedRedirect) {
      localStorage.removeItem('postLoginRedirect');
      return <Navigate to={savedRedirect} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Route for users who are signed in but need to complete profile
const ProfileGateRoute = ({ children }) => {
  const { isSignedIn, needsRegistration, loading, isAuthenticated, customer } = useCustomerAuth();

  // Keep showing spinner while auth state is still being determined
  // Same logic as ProtectedRoute - wait for auth/customer data to stabilize
  const authStateUnknown =
    (isSignedIn && !customer && !needsRegistration) ||
    (!isSignedIn && customer && !needsRegistration);

  if (loading || authStateUnknown) {
    return <FullPageSpinner />;
  }

  // If fully authenticated, go to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // If not signed in at all, go to login
  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }

  // Show profile completion if needed
  if (needsRegistration) {
    return children;
  }

  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Marketing pages - publicly accessible */}
      <Route path="/" element={<Suspense fallback={<PageLoader />}><MarketingHomePage /></Suspense>} />
      <Route path="/demo" element={<Suspense fallback={<PageLoader />}><BookDemoPage /></Suspense>} />

      {/* Auth routes - only for non-authenticated users */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><CustomerRegisterPage /></PublicRoute>} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* Profile completion - for Better Auth users without customer record */}
      <Route path="/complete-profile" element={<ProfileGateRoute><CompleteProfilePage /></ProfileGateRoute>} />

      {/* Legacy URL redirects - redirect old formats to new clean URLs */}
      <Route path="/s/:studioname" element={<LegacyRedirect />} />
      <Route path="/public/schedule/:studioname" element={<LegacyRedirect />} />

      {/* Check-in page - handles its own auth logic */}
      <Route path="/check-in" element={<Suspense fallback={<PageLoader />}><CheckInPage /></Suspense>} />

      {/* Customer routes - all protected, student only */}
      <Route element={<ProtectedRoute><CustomerLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><CustomerDashboardPage /></Suspense>} />
        <Route path="/schedule" element={<Suspense fallback={<PageLoader />}><CustomerSchedulePage /></Suspense>} />
        <Route path="/classes" element={<Suspense fallback={<PageLoader />}><CustomerClassesPage /></Suspense>} />
        <Route path="/community" element={<Suspense fallback={<PageLoader />}><CustomerCommunityPage /></Suspense>} />
        <Route path="/subscription" element={<Suspense fallback={<PageLoader />}><CustomerSubscriptionPage /></Suspense>} />
        <Route path="/settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
        <Route path="/rings" element={<Suspense fallback={<PageLoader />}><RingsPage /></Suspense>} />
      </Route>

      {/* Public schedule view - catch-all for studio URLs (e.g., /sunrise-yoga) */}
      {/* Must be after all other routes but before 404 */}
      <Route path="/:studioname" element={<Suspense fallback={<PageLoader />}><PublicSchedulePage /></Suspense>} />

      {/* 404 - Redirect to home for unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <CustomerAuthProvider>
      <SocketProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </SocketProvider>
    </CustomerAuthProvider>
  );
}

export default App;
